/**
 * ==========================================================================
 *  VIEWMODEL - the bottle in Kimmo's hand
 * ==========================================================================
 *  Every number here moves a photograph of a Karhu III around the lower right
 *  of the screen. None of it touches the simulation.
 *
 *  That is why it is a file of its own rather than a section of
 *  src/config/beer.ts: `simulationFingerprint()` hashes the whole `BEER`
 *  object, so one decorative field added there would retire every time on the
 *  leaderboard for the sake of a wrist angle. Nothing below is hashed, and
 *  nothing below may ever be read from src/sim, src/player or src/world.
 *
 *  ------------------------------------------------------------------------
 *   COORDINATES
 *  ------------------------------------------------------------------------
 *  Screen-relative, not metric:
 *
 *    x        -1 .. 1 across the frame  (1 = the right edge)
 *    y        -1 .. 1 up the frame      (-1 = the bottom edge)
 *    height   how much of the frame's height the bottle is long
 *
 *  BeerViewmodel converts those against the camera's *current* FOV, so the
 *  bottle sits in the same corner at 65 degrees as it does at 110 rather than
 *  swelling to fill half the screen at the narrow end of the slider.
 *
 *  Every pose is the position of the bottle's **mouth**, and the bottle hangs
 *  below it and rotates around it. That is the one point on a bottle that
 *  stays put while you drink from it, so animating anything else means
 *  animating two things at once to keep the neck at your lip.
 * ==========================================================================
 */

export const VIEWMODEL = {
  /**
   * Distance from the eye, metres. Only decides how much the bottle parallaxes
   * against the world - its size on screen is set by `height`, not by this.
   * Well clear of `RENDER.near`, and close enough that the world never wins the
   * depth fight (which it cannot anyway: the bottle draws with depth off).
   */
  depth: 0.5,
  /** Seconds from releasing the bottle to its floor impact. */
  dropDuration: 0.38,

  /** Capped rather than maxed, for the same reason the world textures are. */
  anisotropy: 8,

  /** Carried: low and to the right, clear of where you are actually looking. */
  rest: {
    x: 0.66,
    y: -0.34,
    height: 0.42,
    /** Base hanging out to the right, the way a bottle sits in a loose hand. */
    roll: 0.2,
  },

  /**
   * At the lip.
   *
   * The mouth goes *below* the bottom edge, not to the middle of the frame.
   * Kimmo's mouth is a few centimetres under the camera and you cannot see
   * your own face, so a bottle whose neck is on screen is a bottle hovering in
   * front of you rather than one you are drinking from. Park the neck off the
   * frame and let the tipped body sweep up across the lower right - which is
   * also the half of the picture the label is on.
   */
  drink: {
    x: 0.18,
    y: -0.95,
    height: 0.75,
    /** ~117 degrees. Under 90 and it reads as pouring it down your shirt. */
    roll: 2.05,
    /** Negative tips the base away from the camera, which sells the depth. */
    pitch: -0.3,
  },

  /**
   * How high the mouth swings on its way down to `drink.y`.
   *
   * Without this the middle of the animation is invisible. The neck ends up
   * below the bottom edge, and a bottle that is still upright hangs *down*
   * from its neck - so the whole thing would drop out of frame and then swing
   * back into it once the roll passed vertical. The arc is the hand lifting
   * the bottle in front of your face before the wrist rolls it back.
   */
  raiseArc: 0.45,

  /**
   * The pull, in seconds. Sums to 0.69 - comfortably inside `BEER.cooldown`,
   * so somebody drinking as fast as the simulation lets them gets one
   * continuous motion rather than an animation cut off at the knee.
   */
  sip: {
    /** Hand travel, from the carry to the lip. */
    raise: 0.16,
    /** When the wrist starts rolling. Overlapping the two is the whole trick. */
    tipStart: 0.05,
    /** And how long the roll takes. */
    tip: 0.2,
    /** How long it stays up there. */
    hold: 0.12,
    /** Back down to the carry, slower than it went up. */
    lower: 0.32,
  },

  /** Small wobble while it is tipped up, so the hold is not a freeze frame. */
  glug: { frequency: 26, amplitude: 0.05 },

  /**
   * How much the bottle answers the lurch the sip just bought. It swings
   * *against* the shove, because the arm is the part of Kimmo that is late.
   */
  lurch: { offset: 0.12, roll: 0.22, depth: 0.06 },

  /**
   * Viewmodel lag. The bottle trails the mouse and settles back, which is the
   * cheapest thing there is that makes a hand look attached to a body.
   *
   * `gain` is in screen units per radian/second of turn, so a flick reaches
   * `max` and stops there instead of throwing the bottle off the screen.
   */
  sway: { gain: 0.045, max: 0.3, damping: 11, roll: 0.25 },

  /** Head bob, as a multiple of the camera's own. Both scale with speed. */
  bob: { lateral: 1.5, vertical: 1.2 },

  /** Breathing, so a bottle held still is not a bottle held by a statue. */
  idle: { frequency: 1.1, amplitude: 0.014 },

  /** Fade in and out, per second. Fast enough not to read as a transition. */
  fadeRate: 9,
} as const;

/**
 * ==========================================================================
 *  THE KLAPI, HELD IN THE OTHER HAND
 * ==========================================================================
 *  Cosmetic, exactly like the bottle above and outside the fingerprint for the
 *  same reason: `Kamiina` decides whether Kimmo is carrying wood, and this
 *  only decides what carrying it looks like.
 *
 *  One billet, because one is what he can carry. It used to be two - the pari
 *  Hannu shouts for, stacked crooked in one arm - and that was the right
 *  picture of a toll paid in a single trip past the rack. It is the wrong
 *  picture of this one: the pesä wants two and they go in one at a time, so
 *  what is in the arm on the way to the hatch is exactly one log, and the
 *  question the corner of the screen has to answer is no longer "did I get
 *  them?" but "am I carrying, or am I going back for it?".
 *
 *  Left hand, low, and turned across the body - which is where you actually
 *  carry a split billet you are about to throw. Screen units are the same as
 *  the bottle's: x and y are fractions of the half-screen at `depth`.
 * ==========================================================================
 */
export const KLAPI_VIEWMODEL = {
  /** Metres from the eye. Nearer than the bottle: it is tucked in, not held out. */
  depth: 0.42,
  /** Where it rides, in half-screens. Negative x is the left hand. */
  x: -0.62,
  y: -0.72,
  /** A split billet: longer than it is thick, and thicker than it is wide. */
  length: 0.34,
  thickness: 0.075,
  width: 0.1,
  /** Turned across the body, butt end towards the eye. */
  pitch: 0.22,
  yaw: 0.55,
  roll: -0.18,
  /** How far below its riding height it comes up from, and over how long. */
  raiseFrom: 0.55,
  raiseDuration: 0.16,
  /**
   * The run felt through an armful of wood. Slower and smaller than the
   * bottle's swing, because a log is heavy and a heavy thing does not swing.
   */
  bobRate: 1.15,
  bobAmount: 0.035,
  /** Past this the bob stops getting faster; sprinting is not a blur. */
  bobSpeedCap: 9,
  /** Bark, split face, sawn end. Three browns, and none of them orange. */
  bark: 0x4a3a2a,
  split: 0xb99a72,
  sawn: 0xd8bd93,
} as const;

/**
 * ==========================================================================
 *  THE FIST, AND THE ONE THING IT IS FOR
 * ==========================================================================
 *  Kimmo's right hand, thrown once per run at most: the pane in Einon pirtti's
 *  south window is a wall until he punches it out, and this is the punch.
 *
 *  Cosmetic, and outside the fingerprint like the bottle and the klapit above.
 *  `Ikkuna` decides on a simulation step that the glass went; every number
 *  below only decides what that looked like. A player with the animation
 *  disabled would run exactly the same time.
 *
 *  ------------------------------------------------------------------------
 *   WHY IT LEADS THE SOUND BY A HUNDRED MILLISECONDS
 *  ------------------------------------------------------------------------
 *  The glass breaks on the step F is pressed, which is also the step the cue
 *  plays - so a punch that wound up for a quarter of a second would land well
 *  after the noise it made. The windup and the strike together are 0.1 s,
 *  which is two or three frames of daylight between the crunch and the fist
 *  arriving. Short enough that the ear reads them as one event, and long
 *  enough that the hand does not simply appear at full extension.
 *
 *  The recovery is four times the strike, because that is what a punch looks
 *  like: out fast, back slow, and gone off the bottom of the frame.
 *
 *  ------------------------------------------------------------------------
 *   COORDINATES
 *  ------------------------------------------------------------------------
 *  Screen-relative like the bottle's - x and y are fractions of the half-screen
 *  measured at `depth`, so the hand sits in the same place at 65 degrees of FOV
 *  as it does at 110. The parts themselves are metres at that depth, the way
 *  the klapit are.
 * ==========================================================================
 */
export const FIST_VIEWMODEL = {
  /**
   * Further out than the bottle, and it has to be.
   *
   * The hand is built pointing *at* the camera - the knuckles are the far end
   * and the wrist the near one - so the whole rig eats into the distance
   * between the pivot and the eye, and it eats more of it the further the arm
   * extends. Draw a full forearm at the bottle's 0.5 m and the cuff ends up
   * fifteen centimetres from the lens, where a ten-centimetre box fills two
   * thirds of the screen and the window you are punching is behind it.
   *
   * So: a long depth, a short rig (see `wrist` - there is no forearm at all,
   * only what a real punch shows of one), and a strike that stops short of the
   * crosshair rather than arriving on it.
   */
  depth: 0.62,

  /**
   * Three poses, in the order the hand goes through them. Off the bottom right
   * of the frame, cocked back low and near the eye, then out and up towards
   * the crosshair - stopping short of it, because a fist that lands dead
   * centre is a fist standing between you and the glass it just broke.
   *
   * `scale` is the perspective and `yaw` is what keeps the wrist out of the
   * lens: the arm is turned across the body, so extending it walks the near
   * end towards the bottom-right corner instead of straight at the eye.
   */
  rest: { x: 0.98, y: -1.25, scale: 0.82, roll: 0.52, yaw: 0.62, pitch: -0.34 },
  windup: { x: 0.78, y: -0.74, scale: 0.9, roll: 0.34, yaw: 0.5, pitch: -0.22 },
  strike: { x: 0.38, y: -0.34, scale: 1.14, roll: 0.1, yaw: 0.34, pitch: -0.05 },

  /** The throw, in seconds. Sums to 0.41. */
  timing: { windup: 0.05, strike: 0.05, hold: 0.05, recover: 0.26 },

  /** One right hand, in metres at `depth`. */
  fist: { width: 0.17, height: 0.15, depth: 0.17 },
  /** The four knuckles, as one ridge across the front of it. */
  knuckles: { width: 0.172, height: 0.047, depth: 0.028 },
  /** The thumb, laid across the front of the fingers where a thumb belongs. */
  thumb: { width: 0.064, height: 0.058, depth: 0.095 },
  /**
   * The wrist, and the whole of the arm there is.
   *
   * A forearm would be the length of the fist again and then some, and every
   * centimetre of it is a centimetre nearer the lens - see `depth`. What a
   * punch actually shows you is a hand and the cuff behind it; the elbow is
   * off the bottom of the frame in real life too.
   */
  wrist: { width: 0.135, height: 0.135, depth: 0.085 },

  /** Kimmo's own hand, one of the five tones the standers are built from. */
  skin: 0xd79a72,
  /** Knuckles catch the light, so they are the same tone lifted. */
  highlight: 0xe8bb96,
  /** And what they look like on the way back. See `GLASS_PAIN`. */
  cut: 0x8e2b22,
  sleeveColor: 0x33404f,
} as const;

/**
 * ==========================================================================
 *  THE TUOPPI IN THE OTHER HAND
 * ==========================================================================
 *  From the stack at the end of the baari to the hanat Kimmo is running with
 *  an empty glass, for two seconds under a nozzle he is watching it fill, and
 *  from there to his own chair he is running with a full one. This is that
 *  glass.
 *
 *  It exists for the same reason the klapi does, and answers a harder version
 *  of the same question. The two presses of F are half a hall apart, and what
 *  is in the hand between them is the only thing that says the first one
 *  landed - the prompt is gone the moment the table is behind you. But this
 *  one has to answer a second question as well, because the toll is not "did
 *  I get it?" but "is it *full*?": a man who has taken the glass and run past
 *  the hanat has done exactly half of the errand and has nothing at all to
 *  show for it. So the glass is drawn as what is in it, which is the same
 *  thing every other tuoppi in the level is drawn as - see `tuoppi()` in
 *  src/config/level.ts. Empty reads as glass; full reads as beer with a head
 *  on it, from the corner of the eye, at speed.
 *
 *  Right hand: taking the glass releases the bottle and replaces it here.
 *
 *  Same three tricks as the bottle and the billet (see `BeerViewmodel`):
 *  parented to the camera, drawn with depth testing off and last, and left
 *  out of the fog.
 *
 *  Purely cosmetic. `Sima` in src/world/ decides whether there is a glass in
 *  hand and whether it is full, and nothing here is ever read back.
 * ==========================================================================
 */
export const TUOPPI_VIEWMODEL = {
  /** Metres from the eye. Held out a little further than the billet is tucked. */
  depth: 0.46,
  /**
   * Where it rides, in half-screens. Positive x replaces the bottle on the right.
   *
   * Further in from the edge than the billet's `-0.62`, and it has to be: a
   * klapi is a long thin thing carried lengthwise and it survives being half
   * off the screen, but a glass cut down the middle by the frame is a shape
   * with no width, and width is the whole of how you read what is in it.
   */
  x: 0.5,
  y: -0.74,
  /**
   * The glass itself, in metres at `depth`. Not the level's own `TUOPPI` - a
   * pint held at arm's length reads bigger than a pint on a table twelve
   * metres away, and a viewmodel drawn to world dimensions reads as a doll's
   * one. The proportions are a tuoppi's; the size is what the hand needs.
   */
  radius: 0.047,
  height: 0.16,
  /** The head on a full one, and the ring a finished one leaves. */
  foam: 0.026,
  dregs: 0.012,
  /** Tipped back the way a full glass is carried, and turned across the body. */
  pitch: -0.16,
  yaw: -0.4,
  roll: -0.1,
  /** How far below its riding height it comes up from, and over how long. */
  raiseFrom: 0.55,
  raiseDuration: 0.16,
  /**
   * The run felt through a full glass: quicker than the billet's figure of
   * eight, because this one is light, and smaller than the bottle's swing,
   * because it is being kept level.
   */
  bobRate: 1.45,
  bobAmount: 0.028,
  /** Past this the bob stops getting faster; sprinting is not a blur. */
  bobSpeedCap: 9,
  /**
   * The four colours a tuoppi is made of, taken from the same palette the
   * level draws its own in (`src/render/surfaces.ts`) rather than re-picked
   * by eye: the glass in your hand has to be recognisably the glass that was
   * on the table, and two ambers that nearly match is worse than one.
   */
  beer: 0xc8871c,
  foamColor: 0xeef1ee,
  glass: 0xb6c2c6,
  spill: 0xd0a04a,
} as const;

/** Render-only breakfast plate; excluded from the simulation fingerprint. */
export const PLATE_VIEWMODEL = {
  depth: 0.55,
  x: -0.55,
  y: -0.62,
  radius: 0.15,
  pitch: 0.16,
  roll: -0.04,
  raiseFrom: 0.55,
  raiseDuration: 0.2,
  bobRate: 1.3,
  bobAmount: 0.018,
  bobSpeedCap: 9,
  ceramic: 0xeee9db,
  rim: 0xfffcf2,
  underside: 0xb8b4aa,
  sausage: 0xa6502e,
  steak: 0x683d2b,
  pork: 0xb17b51,
} as const;
